import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  Video,
  Upload,
  X,
  Plus,
  Loader2,
  CheckCircle2,
  Building2,
  Sparkles,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CreditCard,
  Crown
} from 'lucide-react';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateProfile, api, isJobSeeker, isEmployer } = useAuth();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [parsingCv, setParsingCv] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [parsedExpanded, setParsedExpanded] = useState(true);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    location: '',
    bio: '',
    experience_years: '',
    company_name: '',
    company_website: '',
    company_description: '',
    company_size: '',
    industry: ''
  });
  
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || '',
        experience_years: user.experience_years || '',
        company_name: user.company_name || '',
        company_website: user.company_website || '',
        company_description: user.company_description || '',
        company_size: user.company_size || '',
        industry: user.industry || ''
      });
      setSkills(user.skills || []);
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const removeSkill = (index) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const profileData = {
        ...formData,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        skills: isJobSeeker ? skills : undefined
      };
      
      await updateProfile(profileData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploadingCv(true);
    try {
      // Do NOT set Content-Type manually — axios must auto-set multipart/form-data with the boundary
      await api.post('/users/upload-cv', fd);
      toast.success('CV uploaded! Parsing in background…');
      // Auto-trigger parse after upload
      setTimeout(() => handleParseCv(), 2000);
      window.location.reload();
    } catch {
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCv(false);
    }
  };

  const handleParseCv = async () => {
    setParsingCv(true);
    setParsedResult(null);
    try {
      const res = await api.post('/ai/parse-cv');
      setParsedResult(res.data.parsed_data);
      setParsedExpanded(true);
      toast.success('CV parsed! Review extracted data below.');
      // Apply parsed skills to form state
      if (res.data.parsed_data?.skills?.length) {
        setSkills(prev => {
          const combined = [...new Set([...prev, ...res.data.parsed_data.skills])];
          return combined;
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CV parsing failed. Make sure your CV contains selectable text.');
    } finally {
      setParsingCv(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingVideo(true);
    try {
      // Do NOT set Content-Type manually — axios must auto-set multipart/form-data with the boundary
      await api.post('/users/upload-video-cv', formData);
      toast.success('Video CV uploaded successfully!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to upload video CV');
    } finally {
      setUploadingVideo(false);
    }
  };

  const calculateCompletion = () => {
    const fields = isJobSeeker 
      ? ['full_name', 'phone', 'location', 'bio', 'experience_years']
      : ['full_name', 'phone', 'company_name', 'company_description', 'industry'];
    const completed = fields.filter(f => formData[f]).length;
    const hasSkills = isJobSeeker ? skills.length > 0 : true;
    const hasCv = isJobSeeker ? !!user?.cv_url : true;
    
    let total = fields.length;
    let filled = completed;
    if (isJobSeeker) {
      total += 2;
      if (hasSkills) filled++;
      if (hasCv) filled++;
    }
    
    return Math.round((filled / total) * 100);
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account information
          </p>
        </div>

        {/* Completion Progress */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium">Profile Completion</span>
              <span className="text-[#003366] font-bold">{calculateCompletion()}%</span>
            </div>
            <Progress value={calculateCompletion()} className="h-2" />
            {calculateCompletion() < 100 && (
              <p className="text-sm text-muted-foreground mt-2">
                Complete your profile to increase your visibility to {isJobSeeker ? 'employers' : 'candidates'}
              </p>
            )}
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="bg-secondary" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7XXX XXXXXX"
                    data-testid="profile-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, Country"
                    data-testid="profile-location"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio / Summary</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about yourself..."
                  data-testid="profile-bio"
                />
              </div>
            </CardContent>
          </Card>

          {/* Job Seeker Specific */}
          {isJobSeeker && (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Professional Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="experience_years">Years of Experience</Label>
                    <Input
                      id="experience_years"
                      name="experience_years"
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={handleChange}
                      data-testid="profile-experience"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Skills</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a skill"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        data-testid="profile-skill-input"
                      />
                      <Button type="button" variant="outline" onClick={addSkill}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {skills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {skills.map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="pr-1">
                            {skill}
                            <button 
                              type="button"
                              onClick={() => removeSkill(idx)}
                              className="ml-1 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documents */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Documents
                  </CardTitle>
                  <CardDescription>
                    Upload your CV and video introduction
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* CV Upload */}
                  <div className="p-4 border-2 border-dashed rounded-xl">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleCvUpload}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                    />
                    {user?.cv_url ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium">CV Uploaded</p>
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${user.cv_url}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-sm text-[#003366] hover:underline"
                              >View CV</a>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={handleParseCv}
                              disabled={parsingCv}
                            >
                              {parsingCv
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Parsing…</>
                                : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Parse CV with AI</>
                              }
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingCv}
                            >
                              {uploadingCv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Replace'}
                            </Button>
                          </div>
                        </div>

                        {/* Parsed results card */}
                        {parsedResult && (
                          <div className="border border-purple-200 rounded-xl bg-purple-50 overflow-hidden">
                            <button
                              type="button"
                              className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-100 transition-colors"
                              onClick={() => setParsedExpanded(v => !v)}
                            >
                              <span className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                                <Sparkles className="w-4 h-4" />
                                AI Extracted Data
                                <span className="text-xs text-purple-600 font-normal">(review &amp; save profile to apply)</span>
                              </span>
                              {parsedExpanded ? <ChevronUp className="w-4 h-4 text-purple-600" /> : <ChevronDown className="w-4 h-4 text-purple-600" />}
                            </button>
                            {parsedExpanded && (
                              <div className="px-4 pb-4 space-y-3 text-sm">
                                {parsedResult.summary && (
                                  <div>
                                    <p className="font-medium text-purple-800 mb-1">Summary</p>
                                    <p className="text-gray-700 bg-white rounded-lg p-2.5 border border-purple-100">{parsedResult.summary}</p>
                                  </div>
                                )}
                                {parsedResult.skills?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-purple-800 mb-1.5">Skills ({parsedResult.skills.length} found)</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {parsedResult.skills.map((s, i) => (
                                        <span key={i} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium">{s}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {parsedResult.experience?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-purple-800 mb-1.5">Experience</p>
                                    <div className="space-y-1.5">
                                      {parsedResult.experience.map((exp, i) => (
                                        <div key={i} className="bg-white border border-purple-100 rounded-lg p-2.5">
                                          <p className="font-medium text-gray-800">{exp.title} {exp.company && <span className="text-gray-500 font-normal">@ {exp.company}</span>}</p>
                                          {exp.duration && <p className="text-xs text-gray-500 mt-0.5">{exp.duration}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {parsedResult.education?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-purple-800 mb-1.5 flex items-center gap-1.5">
                                      <GraduationCap className="w-3.5 h-3.5" />Education
                                    </p>
                                    <div className="space-y-1.5">
                                      {parsedResult.education.map((edu, i) => (
                                        <div key={i} className="bg-white border border-purple-100 rounded-lg p-2.5">
                                          <p className="font-medium text-gray-800">{edu.degree}</p>
                                          {edu.institution && <p className="text-xs text-gray-500">{edu.institution} {edu.year && `· ${edu.year}`}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <p className="text-xs text-purple-600 bg-purple-100 rounded-lg p-2.5">
                                  Skills have been added to your profile. Click <strong>Save Profile</strong> below to confirm all changes.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="font-medium mb-1">Upload your CV</p>
                        <p className="text-sm text-muted-foreground mb-3">PDF or Word (max 5MB)</p>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingCv}
                          data-testid="upload-cv-btn"
                        >
                          {uploadingCv ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload CV
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Video CV Upload */}
                  <div className="p-4 border-2 border-dashed rounded-xl">
                    <input
                      type="file"
                      ref={videoInputRef}
                      onChange={handleVideoUpload}
                      accept=".mp4,.webm,.mov"
                      className="hidden"
                    />
                    {user?.video_cv_url ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Video CV Uploaded</p>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}${user.video_cv_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#003366] hover:underline"
                            >
                              View Video
                            </a>
                          </div>
                        </div>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                        >
                          {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Replace'}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Video className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="font-medium mb-1">Upload Video CV</p>
                        <p className="text-sm text-muted-foreground mb-3">MP4, WebM or MOV (max 50MB)</p>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                          data-testid="upload-video-btn"
                        >
                          {uploadingVideo ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Video
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Employer Specific */}
          {isEmployer && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      data-testid="profile-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_website">Company Website</Label>
                    <Input
                      id="company_website"
                      name="company_website"
                      value={formData.company_website}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      placeholder="e.g. Technology"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_size">Company Size</Label>
                    <Input
                      id="company_size"
                      name="company_size"
                      value={formData.company_size}
                      onChange={handleChange}
                      placeholder="e.g. 50-100 employees"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_description">Company Description</Label>
                  <Textarea
                    id="company_description"
                    name="company_description"
                    value={formData.company_description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell candidates about your company..."
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full bg-[#E11D48] hover:bg-[#E11D48]/90 h-12"
            disabled={loading}
            data-testid="save-profile-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </form>

        {/* Billing & Subscription */}
        <Card className="mt-8 border-[#003366]/20 bg-gradient-to-br from-[#003366]/5 to-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Billing & Subscription
            </CardTitle>
            <CardDescription>
              Manage your subscription and payment method
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 p-4 rounded-lg bg-white border border-[#003366]/10">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#003366]" />
                  <span className="font-medium">Current Plan</span>
                </div>
                <p className="text-2xl font-bold text-[#003366]">
                  {user?.subscription?.plan_slug ? user.subscription.plan_slug.charAt(0).toUpperCase() + user.subscription.plan_slug.slice(1) : 'Free'}
                </p>
                {user?.subscription?.expires_at && (
                  <p className="text-sm text-muted-foreground">
                    Expires: {new Date(user.subscription.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="space-y-3 p-4 rounded-lg bg-white border border-[#003366]/10">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#003366]" />
                  <span className="font-medium">Billing Email</span>
                </div>
                <p className="text-sm text-muted-foreground">{user?.email || 'Not set'}</p>
              </div>
            </div>

            <div className="space-y-3">
              <Link to="/pricing" className="block">
                <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 h-11">
                  <CreditCard className="w-4 h-4 mr-2" />
                  {user?.subscription?.plan_slug ? 'Upgrade or Change Plan' : 'Choose a Plan'}
                </Button>
              </Link>
            </div>

            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-900">
                💳 <strong>Payment Method:</strong> Stripe card payments are configured. You can securely update your payment information on the checkout page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
